import entryGuideService from './entryGuideService';
import exportRunService from './exportRunService';
import { isMassiveAdjustmentGuide } from '../utils/entryGuideDocumentThreshold';

const POLL_MS = 1500;
const MAX_POLL_ATTEMPTS = 600;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Carga datos de guía para PDF/impresión.
 * Ajustes masivos: segundo plano con callback de progreso.
 */
export async function loadEntryGuideDocumentData(
  guideId,
  { guideType, itemsCount, thresholdSettings = null, onProgress } = {},
) {
  const massive = isMassiveAdjustmentGuide(guideType, itemsCount, thresholdSettings);

  if (!massive) {
    onProgress?.({
      phase: 'sync',
      status: 'processing',
      progress_percent: 30,
      message: 'Cargando datos de la guía…',
    });
    const response = await entryGuideService.getEntryGuide(guideId, { for: 'pdf' });
    if (!response?.success || !response?.data) {
      throw new Error(response?.message || 'No se pudo cargar la guía');
    }
    onProgress?.({
      phase: 'sync',
      status: 'completed',
      progress_percent: 100,
      message: 'Datos listos',
    });
    return response.data;
  }

  onProgress?.({
    phase: 'background',
    status: 'queued',
    progress_percent: 5,
    items_processed: 0,
    items_total: Number(itemsCount) || 0,
    message: 'Iniciando preparación en segundo plano…',
  });

  const prepareRes = await exportRunService.prepareAdjustmentGuideDocument(guideId);
  if (!prepareRes?.success || !prepareRes?.data?.id) {
    throw new Error(prepareRes?.message || 'No se pudo iniciar la preparación de datos');
  }

  let run = prepareRes.data;
  let attempts = 0;

  while (attempts < MAX_POLL_ATTEMPTS) {
    onProgress?.({
      phase: 'background',
      status: run.status,
      progress_percent: run.progress_percent ?? 5,
      items_processed: run.items_processed ?? 0,
      items_total: run.items_total ?? (Number(itemsCount) || 0),
      message:
        run.status === 'queued'
          ? 'En cola, esperando al worker…'
          : `Preparando datos (${run.items_processed ?? 0}/${run.items_total ?? itemsCount} ítems)…`,
      run_id: run.id,
    });

    if (run.status === 'completed') {
      onProgress?.({
        phase: 'background',
        status: 'completed',
        progress_percent: 95,
        items_processed: run.items_total,
        items_total: run.items_total,
        message: 'Descargando datos preparados…',
        run_id: run.id,
      });
      const downloadRes = await exportRunService.download(run.id);
      if (!downloadRes?.success || !downloadRes?.data) {
        throw new Error(downloadRes?.message || 'No se pudieron descargar los datos');
      }
      onProgress?.({
        phase: 'background',
        status: 'completed',
        progress_percent: 100,
        items_processed: run.items_total,
        items_total: run.items_total,
        message: 'Datos listos',
        run_id: run.id,
      });
      return downloadRes.data;
    }

    if (run.status === 'failed') {
      throw new Error(run.error_message || 'Error al preparar los datos de la guía');
    }

    await sleep(POLL_MS);
    const pollRes = await exportRunService.get(run.id);
    if (!pollRes?.success || !pollRes?.data) {
      throw new Error(pollRes?.message || 'No se pudo consultar el progreso');
    }
    run = pollRes.data;
    attempts++;
  }

  throw new Error('Tiempo de espera agotado al preparar los datos de la guía');
}
